import { toEDNStringFromSimpleObject } from "edn-data";
import { deleteData, queryOmegaSingleResult, writeData } from "../../api";
import {
  PagePropKey,
  PagePropOption,
  PagePropsMap,
  PagePropsRecord,
  PathQueryKey,
} from "../../types/page/pageProp";

// Read: the "#props" projection Record via the generic read wrapper (Qo.Public.Api/path-query ->
// Qo.Path.Pq/path-query -> Qo.Pq.Props). A PathQuery by page-id needs no path string.
export const listPageProps = async (
  _appId: string,
  pageId: string,
): Promise<PagePropsMap> => {
  const req = toEDNStringFromSimpleObject(
    { type: PathQueryKey, "record-type": "props", "page-id": pageId, "folder-id": pageId },
    { keysAs: "string" },
  );
  const query = `
    (datastore Qo.Public.Api "omega/query-omega/public/api")
    (Qo.Public.Api/path-query ${req} Response)
    (return [Response])
  `;
  const rec = (await queryOmegaSingleResult(query)) as PagePropsRecord;
  const out: PagePropsMap = {};
  for (const [name, prop] of Object.entries(rec?.props ?? {})) {
    out[name] = { value: prop.value ?? "", "prop-type": prop["prop-type"], options: prop.options ?? [] };
  }
  return out;
};

// The options editor (propertyTypeOptions.tsx SelectOptionMenu.setName) stores
// `color: undefined` on an option; edn-data's toEDNStringFromSimpleObject throws
// `TypeError: Unknown type: undefined` on any undefined value (node_modules/edn-data/dist/generate.js:131),
// which would abort the write before any request is sent. Project to the EDN-safe shape.
const ednSafeOptions = (options: PagePropOption[]): PagePropOption[] =>
  options.map((o) => (o.color === undefined ? { label: o.label } : { label: o.label, color: o.color }));

// A select's value must be one of its option labels (engine hook QO_PAGE_PROP_SELECT_VALUE_INVALID,
// the same policy as Qo.Db.Prop's C12). The edit menu re-sends the prop's PREVIOUS value when only
// the type/options change, so a Text -> Select change would carry a stale text value: normalize
// it to "" (no selection) instead of letting the write be rejected. A blank value is always valid.
const conformingValue = (propType: string, value: string, options: PagePropOption[]): string => {
  if (propType !== "select") return value ?? "";
  return options.some((o) => o.label === value) ? value : "";
};

// Write: one PageProp Record through Qo.Public.Api/write-data (dispatches on "type").
export const setPageProp = async (
  appId: string,
  pageId: string,
  name: string,
  value: string,
  propType = "text",
  options: PagePropOption[] = [],
): Promise<PagePropsMap> => {
  await writeData({
    type: PagePropKey,
    "page-id": pageId,
    name,
    "prop-type": propType,
    options: ednSafeOptions(options),
    value: conformingValue(propType, value ?? "", options),
  });
  return listPageProps(appId, pageId);
};

// Delete: the same Record identity through Qo.Public.Api/delete-data.
export const deletePageProp = async (
  appId: string,
  pageId: string,
  name: string,
): Promise<PagePropsMap> => {
  await deleteData({ type: PagePropKey, "page-id": pageId, name });
  return listPageProps(appId, pageId);
};
